import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Card, Badge, Spinner, Alert, Button, Modal, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import PageLayout from "../../layouts/PageLayout";
import { apiGet, apiPost, apiPut, apiDelete, errorText } from "./sellerApi";

export default function SellerPaymentMethodsPage() {
    const [methods, setMethods] = useState([]);
    const [available, setAvailable] = useState([]);
    const [region, setRegion] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null); // { id?, method, details, is_default }
    const [busy, setBusy] = useState(false);

    const apply = (res) => {
        setMethods(res.data?.data || []);
        setAvailable(res.data?.available || []);
        setRegion(res.data?.region || "");
    };

    const load = useCallback(async () => {
        try {
            apply(await apiGet("/store-seller/payment-methods"));
        } catch (e) {
            setError(errorText(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const config = available.find((a) => a.method === editing?.method);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const body = { method: editing.method, details: editing.details, is_default: !!editing.is_default };
            apply(editing.id ? await apiPut(`/store-seller/payment-methods/${editing.id}`, body) : await apiPost("/store-seller/payment-methods", body));
            setEditing(null);
        } catch (err) {
            Swal.fire({ icon: "error", text: errorText(err) });
        } finally {
            setBusy(false);
        }
    };

    const remove = async (m) => {
        const ok = await Swal.fire({ icon: "warning", title: `Remove ${m.label}?`, showCancelButton: true });
        if (!ok.isConfirmed) return;
        try {
            apply(await apiDelete(`/store-seller/payment-methods/${m.id}`));
        } catch (err) {
            Swal.fire({ icon: "error", text: errorText(err) });
        }
    };

    const makeDefault = async (m) => {
        try {
            apply(await apiPut(`/store-seller/payment-methods/${m.id}`, { details: m.details, is_default: true }));
        } catch (err) {
            Swal.fire({ icon: "error", text: errorText(err) });
        }
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Payment Methods</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to="/dashboard" className="mc-breadcrumb-link">Dashboard</Link></li>
                                <li className="mc-breadcrumb-item">Payment Methods</li>
                            </ul>
                        </div>
                        <Button onClick={() => setEditing({ method: available[0]?.method || "", details: {}, is_default: false })} disabled={!available.length}>
                            Add payment method
                        </Button>
                    </div>
                </Col>
                <Col xl={12}>
                    <p className="text-muted small">
                        {region === "local" ? "Pakistani payment methods." : "International payment methods."} Payouts go to your default method.
                    </p>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {loading ? <div className="text-center py-5"><Spinner animation="border" /></div> : (
                        <Row className="g-3">
                            {methods.map((m) => (
                                <Col md={6} xl={4} key={m.id}>
                                    <Card className="border-0 shadow-sm rounded-4 h-100">
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-start">
                                                <h5 className="fw-bold">{m.label}</h5>
                                                {m.is_default && <Badge bg="primary">Default</Badge>}
                                            </div>
                                            {Object.entries(m.details || {}).map(([k, v]) => (
                                                <div key={k} className="small"><span className="text-muted">{m.detail_labels?.[k] || k}:</span> {v || "-"}</div>
                                            ))}
                                            <div className="d-flex gap-2 mt-3">
                                                <Button size="sm" variant="primary" onClick={() => setEditing({ id: m.id, method: m.method, details: { ...m.details }, is_default: m.is_default })}>Edit</Button>
                                                {!m.is_default && <Button size="sm" variant="secondary" onClick={() => makeDefault(m)}>Make default</Button>}
                                                <Button size="sm" variant="danger" onClick={() => remove(m)}>Remove</Button>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}
                </Col>
            </Row>

            <Modal show={!!editing} onHide={() => setEditing(null)} centered>
                {editing && (
                    <Form onSubmit={save}>
                        <Modal.Header closeButton><Modal.Title>{editing.id ? "Edit" : "Add"} payment method</Modal.Title></Modal.Header>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Method</Form.Label>
                                <Form.Select value={editing.method} disabled={!!editing.id} onChange={(e) => setEditing({ ...editing, method: e.target.value, details: {} })}>
                                    {available.map((a) => <option key={a.method} value={a.method}>{a.label}</option>)}
                                </Form.Select>
                            </Form.Group>
                            {(config?.fields || []).map((f) => (
                                <Form.Group className="mb-2" key={f.name}>
                                    <Form.Label>{f.label}{f.required ? " *" : ""}</Form.Label>
                                    <Form.Control
                                        type={f.name === "email" ? "email" : "text"}
                                        required={f.required}
                                        value={editing.details?.[f.name] || ""}
                                        onChange={(e) => setEditing({ ...editing, details: { ...editing.details, [f.name]: e.target.value } })}
                                    />
                                </Form.Group>
                            ))}
                            <Form.Check className="mt-2" label="Use as default for payouts" checked={!!editing.is_default} onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
                            <Button type="submit" disabled={busy}>Save</Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Modal>
        </PageLayout>
    );
}
